import { HTMLElement, prepareConstructor } from 'application-frame/core/nativePrototype';
import DataBinding from '@af-modules/databinding';

const AUTO_COLLAPSE_CLASS = 'auto-collapse';


// Test via a getter in the options object to see if the passive property is accessed
let supportsPassive = false;

try {
    var opts = Object.defineProperty({}, 'passive', {
        get: function() {
            supportsPassive = true;
        }
    });
    window.addEventListener('test', null, opts);
} catch (e) {}

const PageHeader = {

    widthQuery: null,

    get autoCollapseThreshold() {
        if (this.hasAttribute('auto-collapse-threshold')) {
            const value = this.getAttribute('auto-collapse-threshold');

            return parseFloat(value);
        } else {
            return 0;
        }
    },

    set autoCollapseThreshold(value) {
        this.setAttribute('auto-collapse-threshold', value);
    },

    constructor: function() {
        const instance = HTMLElement.constructor.apply(this);
        const tpl = DataBinding.createTemplateInstance({ template: '#page-header', scope: { view: this }});

        // create shadow root
        instance.attachShadow({ mode: 'open' });

        // place shadow DOM template inside the shadow root
        instance.shadowRoot.appendChild(tpl.node);
        instance.scope = tpl.scope;

        const observer = new MutationObserver(instance.onObserver.bind(instance));

        observer.observe(instance, {
            childList: true
        });

        return instance;
    },

    onObserver() {
        this.childCare();
    },

    childCare() {
        // consume the sloted elements
        this.stickyCollection = Array.prototype.slice.apply(this.querySelectorAll('[sticky]'), []);
        this.scaleCollection = Array.prototype.slice
            .apply(this.querySelectorAll('[scale-to][scale-from]'), []);

        this._calculatePositioning();
        this._setupScaleElements();

        this.onSreenSizeStateChange(this.widthQuery);
    },

    connectedCallback() {
        this.parentNode.addEventListener('scroll', this.onScroll.bind(this), supportsPassive ? { passive: true } : false);

        this.widthQuery = window.matchMedia(`(max-width: ${this.autoCollapseThreshold}px)`);
        this.widthQuery.addListener(this.onSreenSizeStateChange.bind(this));

        return this.childCare();
    },

    _setupScaleElements() {
        this.scaleCollection.forEach(element => {
            element._scaleDifference = parseInt(element.getAttribute('scale-to')) - parseInt(element.getAttribute('scale-from'));
            element.style.transform = `scale(${element.getAttribute('scale-from')})`;
        });
    },

    _calculatePositioning() {
        let collapsedHeight = 0;

        this.stickyCollection.forEach((element, index) => {
            if (index === 0) {
                element._startPos = element.offsetTop;
            } else {
                const prevElement = this.stickyCollection[index-1];

                element._startPos = prevElement._startPos + prevElement.offsetHeight;
            }

            collapsedHeight += element.offsetHeight;
        });

        this._collapsingHeight = this.offsetHeight - collapsedHeight;
        this._lastOffset = 0;
    },

    get _isAutoCollapse() {
        return this.classList.contains(AUTO_COLLAPSE_CLASS);
    },

    /**
     * [onScroll description]
     * @param  {Event} e [description]
     * @return {void}
     */
    onScroll() {
        const autoCollapse = this._isAutoCollapse;
        let scrollTop = this.parentNode.scrollTop;

        if ((autoCollapse && scrollTop > 0) || scrollTop > this._collapsingHeight) {
            scrollTop = this._collapsingHeight;
            this.classList.add('raised');
        } else {
            this.classList.remove('raised');
        }

        if (autoCollapse || this._lastOffset === scrollTop) {
            return;
        }

        if (this._lastOffset === 0) {
            this._calculatePositioning();
        }

        const progress = scrollTop / this._collapsingHeight;

        this.style.transform = `translate3d(0, -${scrollTop}px, 0)`;
        this.stickyCollection.forEach(item => {
            let offset = scrollTop - item._startPos;

            if (offset < 0) {
                offset = 0;
            }

            item.style.transform = `translate3d(0, ${offset}px, 0)`;
        });

        this.scaleCollection.forEach(element => {
            const scale = parseInt(element.getAttribute('scale-from')) + element._scaleDifference * progress;

            element.style.transform = `scale(${scale})`;
        });

        this._lastOffset = scrollTop;
    },

    onSreenSizeStateChange(event) {
        if (event.matches) {
            this.classList.add(AUTO_COLLAPSE_CLASS);
            this._lastOffset = null;
            this.scaleCollection.forEach(element => {
                element._scaleBackup = element.style.transform;
                element.style.transform = '';
            });
        } else {
            this.classList.remove(AUTO_COLLAPSE_CLASS);
            this.onScroll();
        }
    },

    __proto__: HTMLElement,
};


// get the constructor ready do be used by the browser
prepareConstructor(PageHeader);

// register the element as soon as the polyfill is ready
const loadComponent = () => {
    if(window.ShadyCSS) {
        window.ShadyCSS.prepareTemplate(document.querySelector('#page-header'), 'page-header');
    }

    if (window.customElements) {
        window.customElements.define('page-header', PageHeader.constructor);
    }
};

if (window.WebComponents && window.WebComponents.ready) {
    loadComponent();
} else {
    window.addEventListener('WebComponentsReady', loadComponent);
}



//////////////////
// WEBPACK FOOTER
// ./components/PageHeader.js
// module id = 27
// module chunks = 0